import flatMap from 'lodash/flatMap';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';

import {
  ADMIN_BROWSE_SECTION_TYPE,
  DEFAULT_LINK_VALIDATION,
  EMPTY_LIST_URL,
  LISTS_NUM_MIN_LIST_URLS,
  LISTS_HERO_NUM_MIN_LIST_URLS,
  VALIDATION_ERROR_MESSAGE
} from 'app/constants/admin/browse/home';

const V2_LIST_URL_REGEX = /^https?:\/\/([^.]+).*.bibliocommons.com\/v2\/list\/display\/[0-9]+\/([0-9]+)/i;

/*
  Temporary code:
  While in development, we sometimes change the shape of the browseHomePage model.
  In order that these changes do not break the page for internal users (Product, QA)
  who already have saved data to their localStorage, we map old models to new models here
  for compatibility.
*/
const cleanBrowseHomePage = browseHomePage => {
  const { isActive, sections } = browseHomePage;
  const newSections = sections.map(section => {
    const { listUrl, listUrls } = section;

    // ensure all listUrls are objects
    if (typeof listUrl !== 'object') {
      section.listUrl = {
        title: '',
        url: listUrl
      };
    }

    if (listUrls) {
      section.listUrls = section.listUrls.map(nextListUrl => {
        if (typeof nextListUrl === 'object') {
          return nextListUrl;
        }

        return {
          title: '',
          url: nextListUrl
        };
      });
    }

    return section;
  });

  return {
    ...browseHomePage,
    isActive: isActive || false,
    sections: newSections
  };
};

const isValidUrl = urlCandidate => {
  try {
    /* eslint-disable-next-line no-new */
    new URL(urlCandidate);
    return true;
  } catch (_) {
    return false;
  }
};

const isValidListV2Url = urlCandidate => {
  return V2_LIST_URL_REGEX.test(urlCandidate);
};

const parseListUrl = listUrl => {
  try {
    const matches = V2_LIST_URL_REGEX.exec(listUrl);

    const subdomain = matches[1];
    const listId = matches[2];

    return { listId, subdomain };
  } catch {
    return null;
  }
};

const validateLink = linkCandidate => {
  const { label, url } = linkCandidate;

  const isValidLabel = Boolean(label);
  const isValidLinkUrl = isValidUrl(url);

  return {
    isValid: isValidLabel && isValidLinkUrl,
    errorByAttribute: {
      label: isValidLabel ? '' : VALIDATION_ERROR_MESSAGE.INVALID_LABEL,
      url: isValidLinkUrl ? '' : VALIDATION_ERROR_MESSAGE.INVALID_URL
    }
  };
};

const validateCatalogTitlesSection = catalogTitlesSection => {
  const { heading, searchUrl } = catalogTitlesSection;

  const isValidHeading = Boolean(heading);
  const isValidSearchUrl = isValidUrl(searchUrl);
  const isValid = isValidHeading && isValidSearchUrl;

  return {
    isValid,
    errorByAttribute: {
      heading: isValidHeading ? '' : VALIDATION_ERROR_MESSAGE.INVALID_HEADING,
      searchUrl: isValidSearchUrl ? '' : VALIDATION_ERROR_MESSAGE.INVALID_CATALOG_SEARCH_URL
    }
  };
};

const validateLinksSection = linksSection => {
  const { heading, links } = linksSection;

  const isValidHeading = Boolean(heading);

  const hasSomeLinks = links.length > 0;
  const createLinkValidation = hasSomeLinks ? DEFAULT_LINK_VALIDATION : validateLink({ label: '', url: '' });

  const isValid = isValidHeading && createLinkValidation.isValid;

  return {
    isValid,
    errorByAttribute: {
      heading: isValidHeading ? '' : VALIDATION_ERROR_MESSAGE.INVALID_HEADING,
      links: {
        createLink: createLinkValidation
      }
    }
  };
};

const validateListsSection = listsSection => {
  const { heading, listUrls } = listsSection;

  const numUrlsToValidate = Math.max(listUrls.length, LISTS_NUM_MIN_LIST_URLS);
  const listUrlErrors = Array(numUrlsToValidate)
    .fill('')
    .map((_, index) => {
      return isValidListV2Url((listUrls[index] || EMPTY_LIST_URL).url) ? '' : VALIDATION_ERROR_MESSAGE.INVALID_LIST_URL;
    });

  const isHeadingValid = Boolean(heading);
  const isListUrlsValid = listUrlErrors.every(error => !error);

  const isValid = isHeadingValid && isListUrlsValid;

  return {
    isValid,
    errorByAttribute: {
      heading: isHeadingValid ? '' : VALIDATION_ERROR_MESSAGE.INVALID_HEADING,
      listUrls: listUrlErrors
    }
  };
};

const validateListsHeroSection = listsHeroSection => {
  const { listUrls } = listsHeroSection;

  const numUrlsToValidate = Math.max(listUrls.length, LISTS_HERO_NUM_MIN_LIST_URLS);
  const listUrlErrors = Array(numUrlsToValidate)
    .fill('')
    .map((_, index) => {
      return isValidListV2Url((listUrls[index] || EMPTY_LIST_URL).url) ? '' : VALIDATION_ERROR_MESSAGE.INVALID_LIST_URL;
    });

  const isValid = listUrlErrors.every(error => !error);

  return {
    isValid,
    errorByAttribute: {
      listUrls: listUrlErrors
    }
  };
};

const validateListTitlesSection = listTitlesSection => {
  const { listUrl } = listTitlesSection;

  const isValidListUrl = isValidListV2Url(listUrl.url);

  return {
    isValid: isValidListUrl,
    errorByAttribute: {
      listUrl: isValidListUrl ? '' : VALIDATION_ERROR_MESSAGE.INVALID_LIST_URL
    }
  };
};

const validateNavigationSection = navigationSection => {
  const { links } = navigationSection;

  const hasSomeLinks = links.length > 0;
  const createLinkValidation = hasSomeLinks ? DEFAULT_LINK_VALIDATION : validateLink({ label: '', url: '' });
  const isValid = createLinkValidation.isValid;

  return {
    isValid,
    errorByAttribute: {
      links: {
        createLink: createLinkValidation
      }
    }
  };
};

const validateSection = section => {
  const { sectionType } = section;

  switch (sectionType) {
    case ADMIN_BROWSE_SECTION_TYPE.CATALOG_TITLES:
      return validateCatalogTitlesSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.LINKS:
      return validateLinksSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.LISTS:
      return validateListsSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.LISTS_HERO:
      return validateListsHeroSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.LIST_TITLES:
      return validateListTitlesSection(section);
    case ADMIN_BROWSE_SECTION_TYPE.NAVIGATION:
      return validateNavigationSection(section);
    default:
      throw new Error(`Unknown section type: ${sectionType}`);
  }
};

const validateBrowseHomePage = browseHomePage => {
  const { sections } = browseHomePage;

  const validationBySectionId = mapValues(keyBy(sections, 'sectionId'), validateSection);
  const isValid = flatMap(validationBySectionId, validation => validation.isValid).every(Boolean);

  return {
    isValid,
    validationBySectionId
  };
};

export {
  cleanBrowseHomePage,
  isValidUrl,
  isValidListV2Url,
  parseListUrl,
  validateBrowseHomePage,
  validateCatalogTitlesSection,
  validateLink,
  validateLinksSection,
  validateListsHeroSection,
  validateListsSection,
  validateListTitlesSection,
  validateNavigationSection
};
